import React, { useContext } from 'react';
import { ThemeContext } from '../contexts/ThemeContext';
import Calendar from './Calendar';
import {useTournamentAppConfiguration} from "../utils/tournamentconfig/TournamentConfiguration"
import { ResultsContext } from '../contexts/ResultsContext';
import './TournamentInfo.css';

/**
 * TournamentInfo component displays information about a tournament
 * 
 * @param {Object} props - Component props
 * @param {Object} props.tournamentInfo - Tournament information object
 * @param {Function} props.formatDate - Function to format date strings
 * @param {Date} props.lastFetchTime - Time when data was last fetched
 * @param {Function} props.onRefresh - Function to refresh the data
 * @param {string} props.sortOrder - Current sort order
 * @param {Function} props.onSortChange - Function to change sort order
 */
function TournamentInfo({ 
  tournamentInfo, 
  formatDate, 
  lastFetchTime, 
  onRefresh,
  sortOrder,
  onSortChange 
}) {
  const { theme } = useContext(ThemeContext);
  const { setIsTournamentPickerOpen, incrementTournamentId, decrementTournamentId } = useContext(ResultsContext);
  const tournamentAppConfiguration = useTournamentAppConfiguration()

  if (!tournamentInfo) return null;

  // Check if data is stale (more than 10 minutes old)
  const isDataStale = () => {
    if (!lastFetchTime) return false;
    const now = new Date();
    const minutesSinceLastFetch = (now - lastFetchTime) / (1000 * 60);
    return minutesSinceLastFetch > 10;
  };

  return (
    <div className={`tournament-info ${theme}`}>
      <div className="tournament-info-content">
        <div className="tournament-info-main">
          <div className="tournament-info-header">
            <div className="tournament-title-nav">
              {tournamentAppConfiguration.showIdIncrement && (
                <button 
                  className="nav-arrow prev" 
                  onClick={(e) => { e.stopPropagation(); decrementTournamentId(); }}
                  title="Previous Tournament"
                  aria-label="Previous Tournament"
                >
                  ‹
                </button>
              )}
              <div 
                className="title-group" 
                onClick={() => setIsTournamentPickerOpen(true)}
                role="button"
                tabIndex={0}
                title="Click to change tournament"
                onKeyPress={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    setIsTournamentPickerOpen(true);
                  }
                }}
              >
                <h2>
                  {tournamentInfo.title}
                </h2>
                <div className="tournament-dates">
                  {formatDate(tournamentInfo.start)} — {formatDate(tournamentInfo.end)}
                </div>
              </div>

              {tournamentAppConfiguration.showIdIncrement && (
                <button 
                  className="nav-arrow next" 
                  onClick={(e) => { e.stopPropagation(); incrementTournamentId(); }}
                  title="Next Tournament"
                  aria-label="Next Tournament"
                >
                  ›
                </button>
              )}
            </div>
            
            <div className="tournament-sort-container">
              <span className="sort-label">Sort By:</span>
              <div className="sort-button-group">
                <button 
                  className={`sort-button ${sortOrder === 'default' ? 'active' : ''}`}
                  onClick={() => onSortChange('default')}
                >
                  Default
                </button>
                <button 
                  className={`sort-button ${sortOrder === 'alpha' ? 'active' : ''}`}
                  onClick={() => onSortChange('alpha')}
                >
                  A-Z
                </button>
                <button 
                  className={`sort-button ${sortOrder === 'rank' ? 'active' : ''}`}
                  onClick={() => onSortChange('rank')}
                >
                  Rank
                </button>
              </div>
            </div>
          </div>

          <div className="tournament-status-bar">
            <div className="status-item last-updated">
              <span className="status-label">Updated:</span>
              <span className="status-value">{formatDate(tournamentInfo.lastFetched)}</span>
            </div>
            
            {isDataStale() && (
              <div className="status-item stale-warning" onClick={onRefresh}>
                <span className="warning-icon">⚠️</span>
                <span>Stale Data - Refresh</span>
              </div>
            )}
            
            {tournamentInfo.minutesBetweenFetches && (
              <div className="status-item auto-refresh">
                <span className="refresh-icon">🔄</span>
                <span>Auto-refresh: {tournamentInfo.minutesBetweenFetches}m</span>
              </div>
            )}
          </div>
        </div>

        {tournamentAppConfiguration.showCalendar && (
          <div className="tournament-info-calendar">
            <Calendar startDate={tournamentInfo.start} endDate={tournamentInfo.end} />
          </div>
        )}
      </div>
    </div>
  );
}

export default TournamentInfo;